import type {
  TpaStyle,
  TpaStyleParamValue,
} from '@wix/document-services-types';
import Color from 'color';
import type { EditorElement } from '@wix/component-protocol/schema';
import { CSS_PROPERTIES } from '@wix/component-protocol/schema';

import { getWixThemeVariableToColorMap } from '@wix/wix-site-theme-variables';
import {
  ResolvedOOIWidgetStatics,
  StyleType,
} from '@wix/editor-platform-ooi-metadata-resolver';
import { resolveStyleCSSValue } from '@wix/editor-platform-ooi-metadata-resolver/resolvers';
import { STYLE_PROPERTIES_TYPES } from '../constants';

export type ColorStylePropertyType =
  (typeof STYLE_PROPERTIES_TYPES.COLOR)[keyof typeof STYLE_PROPERTIES_TYPES.COLOR];

export type NumberStylePropertyType =
  (typeof STYLE_PROPERTIES_TYPES.NUMBER)[keyof typeof STYLE_PROPERTIES_TYPES.NUMBER];

const GLOBAL_VARIABLE_TO_LEGACY_COLOR_MAP =
  getWixThemeVariableToColorMap() as Record<string, string[]>;

const STYLE_COLOR_TYPE_VALUES = Object.values(STYLE_PROPERTIES_TYPES.COLOR);
const STYLE_COLOR_TYPE_VALUES_SET = new Set<ColorStylePropertyType>(
  STYLE_COLOR_TYPE_VALUES,
);

const isColorStyleProperty = (
  propertyType?: string,
): propertyType is ColorStylePropertyType =>
  STYLE_COLOR_TYPE_VALUES_SET.has(propertyType as ColorStylePropertyType);

const STYLE_NUMBER_TYPE_VALUES = Object.values(STYLE_PROPERTIES_TYPES.NUMBER);
const STYLE_NUMBER_TYPE_VALUES_SET = new Set<NumberStylePropertyType>(
  STYLE_NUMBER_TYPE_VALUES,
);

const isNumberStyleProperty = (
  propertyType?: string,
): propertyType is NumberStylePropertyType =>
  STYLE_NUMBER_TYPE_VALUES_SET.has(propertyType as NumberStylePropertyType);

const isCustomEnumProperty = (
  propertyType?: string,
): propertyType is StyleType.customEnum =>
  propertyType === StyleType.customEnum;

const isDisplayStyleProperty = (propertyType?: string) =>
  propertyType === CSS_PROPERTIES.CSS_PROPERTY_TYPE.display;

function extractCssVariableName(value: string): string | undefined {
  const regex = /var\(--([^)]+)\)/;
  const match = value.match(regex);

  return match?.[1];
}

export class OOIStyleEncoder {
  constructor(
    private manifest: EditorElement,
    private ooiStyleValues: TpaStyle,
    private metadataStatics: ResolvedOOIWidgetStatics,
    private defaultValues: {
      cssProperties: Record<string, any>;
      cssCustomProperties: Record<string, any>;
    },
  ) {}

  decode(styleItemKey: string) {
    const ooiStyles = this.ooiStyleValues?.style ?? {};
    const definition = this.manifest.cssCustomProperties?.[styleItemKey] ?? {};
    const { metadata } = this.metadataStatics.styleParams[styleItemKey] ?? {};

    const dmKey =
      this.metadataStatics?.styleParams?.[styleItemKey]?.key ?? styleItemKey;

    if (isColorStyleProperty(definition.cssPropertyType)) {
      if (!(ooiStyles.colors ?? {})[dmKey]) {
        return this.#genericDecodeValue(styleItemKey);
      }

      const dmStyle = ooiStyles.colors[dmKey];

      if (dmStyle.themeName && dmStyle.themeName.startsWith('var(--wst')) {
        return dmStyle.themeName;
      }

      return dmStyle.value;
    }

    if (isNumberStyleProperty(definition.cssPropertyType)) {
      const _value =
        ooiStyles.numbers?.[dmKey] ??
        this.defaultValues.cssCustomProperties[styleItemKey];

      if (typeof _value === 'undefined' || typeof _value === 'object') {
        return undefined;
      }

      return resolveStyleCSSValue({
        type: definition.cssPropertyType,
        defaultValue: _value,
      });
    }

    if (isCustomEnumProperty(definition.cssPropertyType)) {
      if (metadata?.type === StyleType.legacyFontStyleParamEnum) {
        const dmValue = ooiStyles.fonts?.[styleItemKey];
        // @ts-expect-error hack
        if (dmValue?.fontStyleParam === false) {
          // @ts-expect-error hack
          return dmValue.value;
        }
        return this.defaultValues.cssCustomProperties[styleItemKey];
      }

      const _value =
        // @ts-expect-error DM typings are incorrect https://wix.atlassian.net/browse/EP-7087
        ooiStyles.strings?.[dmKey] ??
        // @ts-expect-error DM typings are incorrect https://wix.atlassian.net/browse/EP-7087
        ooiStyles.strings?.[styleItemKey] ??
        ooiStyles.numbers?.[styleItemKey] ??
        this.defaultValues.cssCustomProperties[styleItemKey];

      if (typeof _value === 'undefined' || typeof _value === 'object') {
        return undefined;
      }

      return _value?.toString();
    }

    if (isDisplayStyleProperty(definition.cssPropertyType)) {
      const _value =
        ooiStyles.booleans?.[dmKey] ??
        this.defaultValues.cssCustomProperties[styleItemKey];

      if (typeof _value === 'undefined' || typeof _value === 'object') {
        return undefined;
      }

      return typeof _value === 'boolean' ? (_value ? 'block' : 'none') : _value;
    }

    return this.#genericDecodeValue(styleItemKey);
  }

  #genericDecodeValue(key: string) {
    const defaultValue = this.defaultValues.cssCustomProperties[key] as any;

    if (typeof defaultValue === 'boolean' || typeof defaultValue === 'number') {
      return defaultValue;
    }

    const preset = defaultValue?.preset as string;

    if (preset) {
      if (preset.startsWith('var(--')) {
        return preset;
      }

      const hexValue = this.ooiStyleValues?.siteColors?.find(
        (siteColor) => siteColor.name === preset,
      )?.value;

      if (!hexValue) {
        return undefined;
      }

      const opacity = defaultValue?.opacity;

      if (typeof opacity === 'number') {
        return Color(hexValue).alpha(opacity).hexa();
      }

      return hexValue;
    }

    return undefined;
  }

  encode(key: string, value: any): TpaStyleParamValue | null {
    const type = this.manifest.cssCustomProperties?.[key]?.cssPropertyType;
    const dmKey = this.metadataStatics?.styleParams?.[key]?.key ?? key;

    if (!type) {
      return null;
    }

    const { metadata } = this.metadataStatics.styleParams[key] ?? {};

    const previousValue = this.ooiStyleValues?.style?.colors?.[dmKey];

    if (isColorStyleProperty(type)) {
      const siteColors = this.ooiStyleValues?.siteColors ?? [];

      try {
        if (typeof value === 'string' && value.startsWith('var(--wst-')) {
          const themeKey = extractCssVariableName(value);

          const legacyColorKey =
            themeKey && GLOBAL_VARIABLE_TO_LEGACY_COLOR_MAP[themeKey]?.[0];

          const legacyColorValue = siteColors.find(
            (siteColor) => siteColor.name === legacyColorKey,
          );

          const color = legacyColorValue?.value
            ? Color(legacyColorValue.value)
            : undefined;

          const defaultOpacity =
            this.manifest.cssCustomProperties?.[key]?.defaultValue?.opacity ??
            1;

          const opacity = previousValue
            ? Color(previousValue.value).alpha()
            : defaultOpacity;

          const newValue = color ? color.alpha(opacity).toString() : value;

          return {
            type: 'color',
            key: dmKey,
            param: {
              value: {
                opacity,
                color: {
                  value: newValue,
                  name: value,
                },
              },
            },
          };
        }

        if (typeof value === 'object' && value.preset) {
          const hexValue = siteColors.find(
            (siteColor) => siteColor.name === value.preset,
          )?.value;

          if (!hexValue) {
            console.error(`no site color name ${value.preset} found.`);
            return null;
          }

          let rgbaValue = Color(hexValue).rgb().toString();

          if (value.opacity) {
            rgbaValue = Color(hexValue).alpha(value.opacity).rgb().toString();
          }

          return {
            type: 'color',
            key: dmKey,
            param: {
              value: {
                opacity: value.opacity,
                rgba: rgbaValue,
                color: {
                  name: value.preset,
                  value: rgbaValue,
                },
              },
            },
          };
        }

        let colorObj = Color(value);
        // Preserve previous opacity if exists and new color is not transparent
        if (previousValue && colorObj.alpha() !== 0) {
          const previousColor = Color(previousValue.value);
          const previousOpacity = previousColor.alpha();

          colorObj = colorObj.alpha(previousOpacity);
        }

        return {
          type: 'color',
          key: dmKey,
          param: {
            value: {
              opacity: colorObj.alpha(),
              rgba: colorObj.rgb().string(),
              color: false,
            },
          },
        };
      } catch (error) {
        return null;
      }
    }

    if (isNumberStyleProperty(type)) {
      const tpaValue = parseFloat(value);
      return {
        type: 'number',
        key: dmKey,
        param: {
          value: tpaValue,
        },
      };
    }

    if (isCustomEnumProperty(type)) {
      if (metadata?.type === StyleType.legacyFontStyleParamEnum) {
        return {
          type: 'font',
          key: dmKey,
          param: {
            value: { fontStyleParam: false, value },
          },
        };
      }

      // TODO add options validation https://wix.atlassian.net/browse/EP-7089
      return {
        type: (this.manifest.cssCustomProperties?.[key]?.customEnum
          ?.cssPropertyType ?? 'string') as TpaStyleParamValue['type'],
        key: dmKey,
        param: {
          value,
        },
      };
    }

    if (isDisplayStyleProperty(type)) {
      return {
        type: 'boolean',
        key: dmKey,
        param: {
          value: value === 'block',
        },
      };
    }

    return null;
  }
}
